import React, { useEffect, useState, useMemo } from 'react';
import { API } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useAuth } from '../lib/auth';
import { RefreshCw, Filter, ShieldAlert, ShieldCheck, History } from 'lucide-react';
import { formatDateTime } from '../lib/brand';

const ACTION_OPTIONS = [
  { value: '__all__', label: 'Alle Aktionen' },
  { value: 'auth.login', label: 'Login' },
  { value: 'auth.register', label: 'Registrierung' },
  { value: 'case.create', label: 'Fall erstellt' },
  { value: 'case.status.update', label: 'Status geändert' },
  { value: 'case.timeline.update', label: 'Timeline aktualisiert' },
  { value: 'case.assign', label: 'Fall zugewiesen' },
  { value: 'case.opponent.update', label: 'Unfallgegner aktualisiert' },
  { value: 'case.vehicle.update', label: 'Fahrzeugdaten aktualisiert' },
  { value: 'ai.analyze', label: 'KI-Analyse' },
  { value: 'ai.report', label: 'KI-Bericht' },
  { value: 'ai.pdf', label: 'PDF erstellt' },
  { value: 'ai.ocr.vehicle', label: 'OCR Zulassungsschein' },
  { value: 'ai.ocr.opponent', label: 'OCR Gegner' },
  { value: 'ai.voice', label: 'KI-Sprachverarbeitung' },
  { value: 'ai.admin_summary', label: 'Admin-Briefing' },
  { value: 'file.upload', label: 'Datei hochgeladen' },
  { value: 'file.delete', label: 'Datei gelöscht' },
  { value: 'note.create', label: 'Notiz erstellt' },
  { value: 'vehicle.create', label: 'Ersatzfahrzeug angelegt' },
  { value: 'vehicle.update', label: 'Ersatzfahrzeug aktualisiert' },
  { value: 'vehicle.delete', label: 'Ersatzfahrzeug gelöscht' },
];

const STATUS_OPTIONS = [
  { value: '__all__', label: 'Alle Status' },
  { value: 'success', label: 'Erfolg' },
  { value: 'failure', label: 'Fehler' },
  { value: 'denied', label: 'Abgelehnt' },
];

function actionLabel(action) {
  const found = ACTION_OPTIONS.find((o) => o.value === action);
  return found?.label || action;
}

function StatusBadge({ status }) {
  if (status === 'failure' || status === 'denied') {
    return (
      <Badge className="bg-rose-100 text-rose-800 border border-rose-200 hover:bg-rose-100">
        <ShieldAlert className="h-3 w-3 mr-1" /> {status === 'denied' ? 'Abgelehnt' : 'Fehler'}
      </Badge>
    );
  }
  return (
    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100">
      <ShieldCheck className="h-3 w-3 mr-1" /> Erfolg
    </Badge>
  );
}

function MetadataPreview({ metadata }) {
  if (!metadata || Object.keys(metadata).length === 0) {
    return <span className="text-slate-400 text-xs">-</span>;
  }
  const entries = Object.entries(metadata).slice(0, 3);
  return (
    <div className="flex flex-wrap gap-1 max-w-xs">
      {entries.map(([k, v]) => (
        <span
          key={k}
          className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200"
        >
          <span className="font-medium">{k}:</span>
          <span className="truncate max-w-[120px]">{String(v)}</span>
        </span>
      ))}
      {Object.keys(metadata).length > 3 && (
        <span className="text-[11px] text-slate-400">+{Object.keys(metadata).length - 3}</span>
      )}
    </div>
  );
}

export default function AdminAuditLogs() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionFilter, setActionFilter] = useState('__all__');
  const [statusFilter, setStatusFilter] = useState('__all__');
  const [emailFilter, setEmailFilter] = useState('');

  const limit = 100;

  const fetchLogs = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const params = { limit };
      if (actionFilter && actionFilter !== '__all__') params.action = actionFilter;
      if (statusFilter && statusFilter !== '__all__') params.status = statusFilter;
      if (emailFilter.trim()) params.actor_email = emailFilter.trim();
      const r = await API.get('/admin/audit-logs', { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Audit-Logs konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const stats = useMemo(() => {
    const counts = { success: 0, failure: 0, denied: 0 };
    items.forEach((it) => {
      counts[it.status] = (counts[it.status] || 0) + 1;
    });
    return counts;
  }, [items]);

  return (
    <div className="space-y-5" data-testid="admin-audit-logs-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading font-semibold text-2xl text-[hsl(var(--primary))] flex items-center gap-2">
            <History className="h-6 w-6" /> Audit-Logs
          </h1>
          <p className="text-sm text-slate-600">
            Vollständige Aktionsprotokollierung gem. DSGVO Art. 30 (Auszug: aktuell {total.toLocaleString('de-AT')} Ereignisse).
          </p>
        </div>
        <Button
          onClick={fetchLogs}
          disabled={loading}
          variant="outline"
          className="gap-2"
          data-testid="audit-refresh-button"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
        </Button>
      </div>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-4 grid gap-4 md:grid-cols-4">
          <div>
            <Label className="text-xs">Aktion</Label>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="mt-1" data-testid="audit-filter-action">
                <SelectValue placeholder="Alle Aktionen" />
              </SelectTrigger>
              <SelectContent>
                {ACTION_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="mt-1" data-testid="audit-filter-status">
                <SelectValue placeholder="Alle Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Benutzer (E-Mail)</Label>
            <Input
              placeholder="z.B. admin@..."
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              className="mt-1"
              data-testid="audit-filter-email"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={fetchLogs}
              disabled={loading}
              className="w-full gap-2 bg-[hsl(var(--primary))] hover:bg-[hsl(210_73%_12%)]"
              data-testid="audit-apply-filters"
            >
              <Filter className="h-4 w-4" /> Anwenden
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-3 gap-3">
        <Card className="rounded-2xl border-slate-200">
          <CardContent className="p-4">
            <div className="text-xs text-slate-500">Erfolg</div>
            <div className="text-2xl font-heading font-semibold text-emerald-700 mt-1" data-testid="audit-stats-success">{stats.success}</div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-slate-200">
          <CardContent className="p-4">
            <div className="text-xs text-slate-500">Fehler</div>
            <div className="text-2xl font-heading font-semibold text-rose-700 mt-1" data-testid="audit-stats-failure">{stats.failure}</div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-slate-200">
          <CardContent className="p-4">
            <div className="text-xs text-slate-500">Abgelehnt</div>
            <div className="text-2xl font-heading font-semibold text-amber-700 mt-1" data-testid="audit-stats-denied">{stats.denied || 0}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-2xl border-slate-200 overflow-hidden">
        <CardContent className="p-0">
          {error && (
            <div className="p-4 bg-rose-50 border-b border-rose-200 text-sm text-rose-800" data-testid="audit-error">
              {error}
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">Zeit</TableHead>
                  <TableHead>Aktion</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Benutzer</TableHead>
                  <TableHead>Ziel</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10 text-slate-500">
                      Lade Audit-Ereignisse...
                    </TableCell>
                  </TableRow>
                )}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10 text-slate-500" data-testid="audit-empty">
                      Keine Ereignisse gefunden. Sobald Aktionen passieren, erscheinen sie hier in Echtzeit.
                    </TableCell>
                  </TableRow>
                )}
                {items.map((row) => (
                  <TableRow key={row.id} data-testid={`audit-row-${row.id}`}>
                    <TableCell className="whitespace-nowrap text-xs text-slate-700">
                      {formatDateTime(row.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-slate-900">{actionLabel(row.action)}</span>
                        <span className="text-[11px] font-mono text-slate-400">{row.action}</span>
                      </div>
                    </TableCell>
                    <TableCell><StatusBadge status={row.status} /></TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm text-slate-800 truncate max-w-[180px]">
                          {row.actor_email || (row.actor_id ? row.actor_id.slice(0, 8) + '...' : 'Anonym')}
                        </span>
                        {row.actor_role && (
                          <span className="text-[11px] text-slate-500 uppercase tracking-wide">{row.actor_role}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {row.target_type ? (
                        <div className="flex flex-col">
                          <span className="text-xs text-slate-500">{row.target_type}</span>
                          {row.target_id && (
                            <span className="text-[11px] font-mono text-slate-700 truncate max-w-[160px]">
                              {row.target_id.slice(0, 8)}...
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 whitespace-nowrap">{row.ip || '-'}</TableCell>
                    <TableCell><MetadataPreview metadata={row.metadata} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {items.length > 0 && (
            <div className="px-4 py-3 border-t border-slate-200 text-xs text-slate-500 flex items-center justify-between">
              <span>Zeige {items.length} von {total.toLocaleString('de-AT')} Ereignissen</span>
              <span>Sortierung: Neueste zuerst</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
